import { useGetList } from "ra-core";

import type { Contact, ContactNote } from "@/components/atomic-crm/types";
import { DashboardStepper } from "@/components/atomic-crm/dashboard/DashboardStepper";
import { DashboardActivityLog } from "@/components/atomic-crm/dashboard/DashboardActivityLog";
import { DealsChart } from "@/components/atomic-crm/dashboard/DealsChart";
import { HotContacts } from "@/components/atomic-crm/dashboard/HotContacts";
import { TasksList } from "@/components/atomic-crm/dashboard/TasksList";
import { Welcome } from "@/components/atomic-crm/dashboard/Welcome";
import { KpiCards } from "./KpiCards";
import { useBldrOsStats } from "./useBldrOsStats";
import { BldrOsOnboarding } from "../onboarding/BldrOsOnboarding";
import { useOnboardingStatus } from "../onboarding/useOnboardingStatus";
import { QuickAddLeadButton } from "../contacts/QuickAddLeadButton";

/**
 * BldrOS Desktop Dashboard.
 *
 * Renders:
 * 1. Onboarding (if org not configured)
 * 2. DashboardStepper (if no contacts/notes — Atomic CRM onboarding)
 * 3. BldrOS KPI cards + original dashboard content
 */
export const BldrOsDashboard = () => {
  const { needsOnboarding, isPending: onboardingPending } =
    useOnboardingStatus();
  const stats = useBldrOsStats();

  const {
    data: dataContact,
    total: totalContact,
    isPending: isPendingContact,
  } = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: 1 },
  });

  const { total: totalContactNotes, isPending: isPendingContactNotes } =
    useGetList<ContactNote>("contact_notes", {
      pagination: { page: 1, perPage: 1 },
    });

  const { total: totalDeal, isPending: isPendingDeal } = useGetList<Contact>(
    "deals",
    { pagination: { page: 1, perPage: 1 } }
  );

  const isPending =
    isPendingContact ||
    isPendingContactNotes ||
    isPendingDeal ||
    onboardingPending;

  if (isPending) return null;

  // BldrOS onboarding takes priority
  if (needsOnboarding) {
    return <BldrOsOnboarding />;
  }

  // Atomic CRM onboarding (no contacts yet)
  if (!totalContact) {
    return <DashboardStepper step={1} />;
  }
  if (!totalContactNotes) {
    return <DashboardStepper step={2} contactId={dataContact?.[0]?.id} />;
  }

  // Full dashboard: BldrOS KPIs + original layout
  return (
    <div>
      {!stats.isPending && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold">Dashboard</h2>
            <QuickAddLeadButton />
          </div>
          <KpiCards stats={stats} />
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mt-1">
        <div className="md:col-span-3">
          <div className="flex flex-col gap-4">
            {import.meta.env.VITE_IS_DEMO === "true" ? <Welcome /> : null}
            <HotContacts />
          </div>
        </div>
        <div className="md:col-span-6">
          <div className="flex flex-col gap-6">
            {totalDeal ? <DealsChart /> : null}
            <DashboardActivityLog />
          </div>
        </div>
        <div className="md:col-span-3">
          <TasksList />
        </div>
      </div>
    </div>
  );
};
